import { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { CellContext, type ColumnDef } from '@tanstack/react-table';
import useOffers from 'api/useOffers';
import type { Offer } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { EOfferStatus, OfferType, SortingOffersValue } from 'constants/enums';
import { usePageTypeByFeatureFlag } from 'hooks/usePageTypeByFF';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useTableSorting } from 'hooks/useTableSorting';
import { permissionsUtil } from 'utils/permissionsUtil';
import {
  appearanceUtils,
  statusMapping,
  statusUtils
} from 'utils/scheduleUtils';
import { statusFilterOptions } from 'utils/selectOptionsUtils';
import { EToasterStatus, showToast } from 'utils/showToast';

import Chip from 'components/Chip/Chip';
import FilterDropdownCard from 'components/Filter/FilterDropdownCard';
import FilterSearchCard from 'components/Filter/FilterSearchCard';

import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import PortalHeader from '../../components/PortalHeader/PortalHeader';

import type { RowData } from './types';

const ProgressBarsTable = () => {
  const navigate = useNavigate();
  const { publisherId } = useParams();
  const location = useLocation();
  const { page, rows } = usePaginationFromURL('offers');
  const [currentPage, setCurrentPage] = useState(page);
  const { sorting } = useTableSorting(SortingOffersValue.CREATED_AT);

  const { getOffers, deleteOffer, toggleOfferStatus } = useOffers({
    offerId: undefined,
    offerType: OfferType.PROGRESS_BAR,
    customPagination: { currentPage, rowsPerPage: rows },
    sorting
  });
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [skuFilterValue, setSkuFilterValue] = useState<string>('');
  const [statusFilterValue, setStatusFilterValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { isPageDisabled } = usePageTypeByFeatureFlag('Progress Bar');

  const isEmpty = getOffers.data?.offers && getOffers.data.offers.length === 0;
  const totalCount = getOffers.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / rows);

  useEffect(() => {
    setIsLoading(
      getOffers.isFetching || getOffers.isRefetching || getOffers.isLoading
    );
  }, [getOffers.data?.offers, getOffers.isRefetching]);

  useEffect(() => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Progress bar deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete progress bar',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setSelectedOffer(null);
      }
    });
  }, [selectedOffer]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rows));
    queryParams.set('sortValue', sorting.sortValue);
    queryParams.set('direction', sorting.direction);

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getOffers.refetch();
  }, [currentPage, rows, sorting]);

  const isActionEnabled = useMemo(
    () =>
      permissionsUtil.isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.MANAGE,
        publisherId
      ),
    [publisherId]
  );

  const canUpdateOfferStatus = useMemo(
    () =>
      permissionsUtil.isActionEnabled(
        null,
        EPermissionGroup.OFFERS,
        EPermissionAction.UPDATE_OFFER_STATUS,
        publisherId
      ),
    [publisherId]
  );

  const filteredData =
    getOffers?.data?.offers.filter((offer: Offer) => {
      const isSkuMatch = offer.publisherOfferId
        .toLowerCase()
        .includes(skuFilterValue.toLowerCase());

      const offerStatus = statusUtils.getOfferStatus(offer, offer.active);

      return (
        (!statusFilterValue ||
          statusFilterValue === 'All' ||
          statusFilterValue === offerStatus ||
          (statusFilterValue === EOfferStatus.INACTIVE &&
            offerStatus === EOfferStatus.INACTIVE)) &&
        isSkuMatch
      );
    }) || [];

  const columns: ColumnDef<RowData, unknown>[] = [
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: (cell: CellContext<RowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (cell: CellContext<RowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'offerUi.name',
      header: 'Design Name',
      cell: (cell: CellContext<RowData, unknown>) => (
        <span className="truncate">{cell.row.original?.offerUi?.name}</span>
      )
    },
    {
      accessorKey: 'missions',
      header: 'Missions',
      cell: (cell: CellContext<RowData, unknown>) =>
        cell.row.original.productsSequence?.length || 0
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<RowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'nextAppearance',
      header: 'Next Appearance',
      cell: (cell: CellContext<RowData, unknown>) => {
        const schedule = cell.row.original?.schedule;
        if (schedule?.permanent) return '-';
        return appearanceUtils.getNextUpcomingTime(schedule?.timeFrames) || '-';
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: (cell: CellContext<RowData, unknown>) => {
        const { original } = cell.row;
        return (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={original}
              actions={[
                {
                  label: original.active ? 'Deactivate' : 'Activate',
                  handler: () =>
                    handleToggleActiveStatus(original.offerId, original.active),
                  disabled: !canUpdateOfferStatus
                },
                {
                  label: 'Edit',
                  handler: () => navigate(`./form/${original.offerId}`),
                  disabled: !isActionEnabled
                },
                {
                  label: 'Duplicate',
                  handler: () => navigate(`./dup/${original.offerId}`),
                  disabled: !isActionEnabled
                },
                {
                  label: 'Delete',
                  handler: () => {
                    setSelectedOffer(original.offerId);
                  },
                  disabled: !isActionEnabled
                }
              ]}
            />
          </div>
        );
      }
    }
  ];

  const handleToggleActiveStatus = async (offerId: string, active: boolean) => {
    toggleOfferStatus.mutate(
      { active: !active, offerId },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Progress bar deactivated successfully'
              : 'Progress bar activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing progress bar',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  return (
    <div
      className={`${isPageDisabled ? 'opacity-50 pointer-events-none' : 'opacity-100 pointer-events-auto'}`}
    >
      <PortalHeader
        title="Progress Bars"
        primaryButtonText="Create New Progress Bar"
        onPrimaryButtonClick={() => navigate('./form')}
        isCreateButton
        primaryButtonState={{
          hidden: !isActionEnabled
        }}
      />
      {!isEmpty && (
        <div className="flex px-9 gap-3">
          <FilterSearchCard
            header="External ID"
            filterValue={skuFilterValue}
            setFilterValue={setSkuFilterValue}
            isLabelSearchBy
          />
          <FilterDropdownCard
            header="Status"
            filterValue={statusFilterValue}
            setFilterValue={setStatusFilterValue}
            options={statusFilterOptions}
          />
        </div>
      )}
      {isLoading ? (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      ) : (
        <div className="px-9 pb-9 pt-2.5">
          <DataTable
            data={filteredData}
            columns={columns}
            rowIdIdentifier="offerId"
            rowCount={totalCount}
            rowsPerPage={rows}
            showPagination={totalPages > 1}
            manualPagination
            pageIndex={currentPage}
            pageCount={totalPages}
            onPageChange={setCurrentPage}
            emptyStateConfig={{
              title: 'Progress bar',
              isEmpty,
              description:
                'Build your first progress bar now and boost players engagement.',
              disableButton: !isActionEnabled,
              onClick: () => navigate('./form')
            }}
            bordered
          />
        </div>
      )}
    </div>
  );
};

export default ProgressBarsTable;
